/**
 * PositionCard — per-coarse-position card in the Opening Likelihood grid.
 *
 * Anatomy (top to bottom):
 *   - 3px tier accent band (hairline color when no data)
 *   - Header: label + long_label, compare descriptor, score badge
 *   - Body (when data): "Class of N graduating" + PercentileTickInline + ClassYearBar
 *   - Player-listing disclosure (when data + matching players):
 *       · "View players (N)" collapsed by default
 *       · Expands PlayerMiniList filtered to this coarse position group
 *       · Hidden entirely when 0 players match
 *   - Fine section (when data + fine present):
 *       · FallbackRow if has_fine_data = false
 *       · FineRows (collapsible) if has_fine_data = true
 *
 * Matches detail-card.jsx PositionCard, FallbackRow, FineRows, FineRow.
 *
 * Badge selection:
 *   no_data / score null → NoDataBadge
 *   fine.has_fine_data = false → FallbackBadge (coarse-only estimate)
 *   otherwise → ScoreBadge
 */
import React, { useState } from 'react';
import type { PositionDetail, FineRowDetail, PlayerDetail, ClassCounts } from '../../api/school';
import { ScoreBadge } from '../ScoreBadge';
import { FallbackBadge } from '../FallbackBadge';
import { NoDataBadge } from './NoDataBadge';
import { ClassYearBar, type HighlightBucket } from './ClassYearBar';
import { PercentileTickInline } from './PercentileTickInline';
import { PlayerMiniList } from './PlayerMiniList';
import { IconChevronDown, IconChevronUp, IconInfo } from './icons';
import { type SportSlug, SportPositionTaxonomy } from '../../constants/positions';
import { getCachedTaxonomy } from '../../api/positions';

// Fallback FINE_UNDER_COARSE for when the cache is cold (e.g. direct component
// rendering in tests without pre-populating the cache). Returns empty set,
// causing the player filter to fall back to direct slug equality only.
function getFineSlugsForCoarse(sport: SportSlug | undefined, posSlug: string): Set<string> {
  if (!sport) return new Set<string>();
  const taxonomy = getCachedTaxonomy(sport);
  if (!taxonomy) return new Set<string>();
  return new Set(taxonomy.FINE_UNDER_COARSE[posSlug] ?? []);
}

// WI-36 cohort filter: a cohort is "graduated" relative to a target year when
// they would have left the roster by then. years_out=0 keeps everyone (no one
// has graduated yet from the current snapshot's perspective). years_out=1
// drops SR+GR (they leave after this season). +2 also drops JR. +3 also drops
// SO. UNK class-year players are never dropped — we don't know their cohort.
function graduatedClassYears(yearsOut: number): Set<string> {
  const grad = new Set<string>();
  if (yearsOut >= 1) { grad.add('SR'); grad.add('GR'); }
  if (yearsOut >= 2) { grad.add('JR'); }
  if (yearsOut >= 3) { grad.add('SO'); }
  return grad;
}

function filterCounts(counts: ClassCounts, graduated: Set<string>): ClassCounts {
  return {
    SR: graduated.has('SR') ? 0 : counts.SR,
    GR: graduated.has('GR') ? 0 : counts.GR,
    JR: graduated.has('JR') ? 0 : counts.JR,
    SO: graduated.has('SO') ? 0 : counts.SO,
    FR: graduated.has('FR') ? 0 : counts.FR,
  };
}

interface Props {
  pos: PositionDetail;
  targetYear: number;
  mobile?: boolean;
  /** When true, fine-position section is open by default (default true). */
  defaultOpen?: boolean;
  /**
   * All players for this school/sport. The card will filter to those matching
   * this coarse position group (primary, secondary, or tertiary position).
   * Optional — when absent or empty, no player disclosure is rendered.
   */
  players?: PlayerDetail[];
  /**
   * Sport slug for fine→coarse mapping. Football players are tagged at fine
   * positions (qb, rb_fb, dl, lb_fb, k, etc.), so the player filter must
   * resolve their coarse parent via FINE_UNDER_COARSE before comparing to
   * pos.slug. Soccer players post-WI-31 are tagged at coarse slugs directly,
   * but soccer schools that publish fine positions still need the lookup.
   * Optional — when omitted, falls back to direct slug equality only.
   */
  sport?: SportSlug;
  /**
   * WI-36: which class bucket to visually highlight in ClassYearBar.
   * Derived from years_out = targetYear - currentYear in SchoolDetailPage.
   */
  highlightBucket?: HighlightBucket | null;
  /**
   * WI-36 follow-up: years between current calendar year and targetYear.
   * Used to filter players (and ClassYearBar / fine-row counts) so cohorts
   * that have already graduated by the target year don't appear in the
   * roster listing. When omitted, no filtering is applied.
   */
  yearsOut?: number;
}

/** Tier accent hex — matches tierAccent() in detail-atoms.jsx. */
function tierAccentHex(tier: PositionDetail['tier']): string {
  switch (tier) {
    case 'high':   return '#1B5E42';
    case 'medium': return '#A0671E';
    case 'low':    return '#9C3838';
    default:       return '#E9E6E1'; // hairline color for no-data
  }
}

export function PositionCard({ pos, targetYear, mobile = false, defaultOpen = true, players, sport, highlightBucket, yearsOut = 0 }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [playersOpen, setPlayersOpen] = useState(false);

  const noData = pos.no_data || pos.score == null;
  const accent = tierAccentHex(pos.tier);
  const isFallback = !noData && pos.fine !== null && pos.fine.has_fine_data === false;
  const hasFineRows = !noData && pos.fine !== null && pos.fine.has_fine_data === true && (pos.fine.rows?.length ?? 0) > 0;

  const graduatedYears = graduatedClassYears(yearsOut);
  const filteredCounts = filterCounts(pos.class_counts, graduatedYears);

  // Set of fine slugs that roll up to this card's coarse slug (sport-aware).
  // Football players are tagged at fine positions like qb/rb_fb/dl/lb_fb/k,
  // so a `defense` card must match players with `position === 'dl'` etc.
  // Soccer schools that publish fine positions get the same treatment.
  const fineSlugsForCoarse: Set<string> = getFineSlugsForCoarse(sport, pos.slug);

  function playerMatchesCoarse(p: PlayerDetail): boolean {
    return (
      p.position === pos.slug ||
      p.secondary_position === pos.slug ||
      p.tertiary_position === pos.slug ||
      fineSlugsForCoarse.has(p.position) ||
      (p.secondary_position !== null && fineSlugsForCoarse.has(p.secondary_position)) ||
      (p.tertiary_position !== null && fineSlugsForCoarse.has(p.tertiary_position))
    );
  }

  // Filter players to those whose primary, secondary, or tertiary position
  // matches this card's coarse slug — either directly or via fine-under-coarse rollup,
  // AND whose class year has not already graduated by the target year (WI-36 follow-up).
  const matchingPlayers: PlayerDetail[] = !noData && players && players.length > 0
    ? players.filter((p) => playerMatchesCoarse(p) && !graduatedYears.has(p.class_year))
    : [];

  const hasMatchingPlayers = matchingPlayers.length > 0;
  const playerPanelId = `player-list-${pos.slug}`;

  const px = mobile ? 'px-3.5' : 'px-[18px]';

  return (
    <section
      aria-label={`${pos.long_label} opening likelihood`}
      className={[
        'bg-surface-card border border-hairline rounded-[10px] overflow-hidden relative',
        noData ? 'opacity-85' : '',
      ].join(' ')}
    >
      {/* 3px tier accent band */}
      <div
        style={{ height: 3, background: noData ? '#E9E6E1' : accent }}
        aria-hidden="true"
      />

      {/* Card header */}
      <div className={`flex items-start gap-3 ${px} ${mobile ? 'pt-3.5 pb-2.5' : 'pt-4 pb-3'}`}>
        <div className="flex-1 min-w-0">
          {/* Position label + long_label */}
          <div className="flex items-baseline gap-2">
            <span className={`${mobile ? 'text-[13px]' : 'text-[14px]'} font-bold tracking-[0.08em] text-gray-900`}>
              {pos.label}
            </span>
            <span className={`${mobile ? 'text-[12px]' : 'text-[13px]'} text-gray-600`}>
              {pos.long_label}
            </span>
          </div>
          {/* Compare descriptor or no-data note */}
          <div className="mt-1 text-[12px] text-gray-600 flex items-center gap-1.5 flex-wrap">
            {noData ? (
              <span className="text-gray-500">No public roster data yet</span>
            ) : (
              pos.compare && (
                <span>{pos.compare.descriptor}</span>
              )
            )}
          </div>
        </div>

        {/* Score badge */}
        <div className="flex-shrink-0">
          {noData ? (
            <NoDataBadge mobile={mobile} />
          ) : isFallback ? (
            <FallbackBadge score={Math.round(pos.score!)} size={mobile ? 'sm' : 'md'} parentPosition={pos.slug} />
          ) : (
            <ScoreBadge score={pos.score!} tier={pos.tier!} size={mobile ? 'sm' : 'md'} />
          )}
        </div>
      </div>

      {/* Body — class year section (only when data is available) */}
      {!noData && (
        <>
          <hr className="border-t border-hairline" />
          <div className={`${px} ${mobile ? 'pt-3 pb-2.5' : 'pt-3.5 pb-3'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-semibold tracking-[0.06em] text-gray-700 uppercase">
                Class of {targetYear} graduating
              </div>
              {pos.compare && (
                <PercentileTickInline
                  percentile={pos.compare.percentile}
                  tier={pos.tier}
                  mobile={mobile}
                />
              )}
            </div>
            <ClassYearBar counts={filteredCounts} height={10} showLegend={false} highlightBucket={highlightBucket} />
          </div>
        </>
      )}

      {/* Player listing disclosure — slotted between ClassYearBar and fine section */}
      {!noData && hasMatchingPlayers && (
        <>
          <hr className="border-t border-hairline" />
          <button
            type="button"
            onClick={() => setPlayersOpen((v) => !v)}
            aria-expanded={playersOpen}
            aria-controls={playerPanelId}
            className={`w-full flex items-center justify-between ${px} ${mobile ? 'py-2.5' : 'py-[11px]'} bg-transparent text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset`}
          >
            <span className="flex items-center gap-2 text-[12px] text-gray-700 font-semibold">
              <span className="text-gray-500" aria-hidden="true">
                {playersOpen ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
              </span>
              {playersOpen ? 'Hide players' : `View players (${matchingPlayers.length})`}
            </span>
          </button>

          <div
            id={playerPanelId}
            role="region"
            aria-label={`${pos.long_label} players`}
            hidden={!playersOpen}
          >
            <div className={`${px} pb-2`}>
              <PlayerMiniList players={matchingPlayers} />
            </div>
          </div>
        </>
      )}

      {/* Fine-position section */}
      {!noData && pos.fine && (
        <>
          <hr className="border-t border-hairline" />
          {isFallback ? (
            <FallbackRow
              applies_to={pos.fine.applies_to ?? []}
              long_label={pos.long_label}
              mobile={mobile}
              px={px}
            />
          ) : hasFineRows ? (
            <FineRows
              rows={pos.fine.rows!}
              open={open}
              setOpen={setOpen}
              mobile={mobile}
              px={px}
              graduatedYears={graduatedYears}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

// ── FallbackRow ───────────────────────────────────────────────────────────────

interface FallbackRowProps {
  applies_to: string[];
  long_label: string;
  mobile: boolean;
  px: string;
}

function FallbackRow({ applies_to, long_label, mobile, px }: FallbackRowProps) {
  const padding = mobile ? 'py-2.5' : 'py-3';
  return (
    <div
      className={`${px} ${padding} flex items-center gap-2.5 text-[12px] text-gray-600 bg-surface-sunken`}
    >
      <span className="inline-flex text-brand-accent flex-shrink-0" aria-hidden="true">
        <IconInfo size={14} />
      </span>
      <span>
        <span className="text-gray-700">Fine-position breakdown not posted</span>
        <span className="text-gray-500"> — {long_label.toLowerCase()} score applies to </span>
        {applies_to.map((label, i) => (
          <React.Fragment key={label}>
            <span className="font-semibold text-gray-700 tabular-nums">{label}</span>
            {i < applies_to.length - 1 && (
              <span className="text-gray-400"> · </span>
            )}
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}

// ── FineRows ──────────────────────────────────────────────────────────────────

interface FineRowsProps {
  rows: FineRowDetail[];
  open: boolean;
  setOpen: (v: boolean) => void;
  mobile: boolean;
  px: string;
  graduatedYears: Set<string>;
}

function FineRows({ rows, open, setOpen, mobile, px, graduatedYears }: FineRowsProps) {
  const total = rows.length;
  const togglePadding = mobile ? 'py-2.5' : 'py-[11px]';
  const contentPadding = mobile ? 'pb-3' : 'pb-3.5';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`w-full flex items-center justify-between ${px} ${togglePadding} bg-transparent text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset`}
      >
        <span className="flex items-center gap-2 text-[12px] text-gray-700 font-semibold">
          <span className="text-gray-500" aria-hidden="true">
            {open ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
          </span>
          By specialty
          <span className="text-gray-500 font-medium">
            · {total} {total === 1 ? 'role' : 'roles'}
          </span>
        </span>
      </button>

      {open && (
        <div className={`${px} ${contentPadding}`}>
          <div
            className="grid items-center"
            style={{
              gridTemplateColumns: mobile ? '36px 1fr 38px' : '44px 1fr 60px',
              rowGap: 4,
            }}
          >
            {rows.map((r) => (
              <FineRowItem key={r.slug} row={r} mobile={mobile} graduatedYears={graduatedYears} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ── FineRowItem ───────────────────────────────────────────────────────────────

function FineRowItem({ row, mobile, graduatedYears }: { row: FineRowDetail; mobile: boolean; graduatedYears: Set<string> }) {
  if (row.no_data) {
    return (
      <>
        <span className="text-[11px] font-bold text-gray-500 tracking-[0.06em]">{row.label}</span>
        <span className="text-[11.5px] text-gray-500 pl-2">No data on this role yet</span>
        <span />
      </>
    );
  }

  const score = row.score ?? 0;
  const fill = Math.max(4, Math.min(100, score));
  // Tier accent for the bar fill
  const accent = row.tier === 'high' ? '#1B5E42' : row.tier === 'medium' ? '#A0671E' : '#9C3838';

  const counts = row.class_counts;
  const summaryParts = counts ? [
    counts.SR && !graduatedYears.has('SR') ? `${counts.SR} SR` : null,
    counts.GR && !graduatedYears.has('GR') ? `${counts.GR} GR` : null,
    counts.JR && !graduatedYears.has('JR') ? `${counts.JR} JR` : null,
    counts.SO && !graduatedYears.has('SO') ? `${counts.SO} SO` : null,
    counts.FR && !graduatedYears.has('FR') ? `${counts.FR} FR` : null,
  ].filter(Boolean) : [];
  const summary = summaryParts.join(' · ');

  return (
    <>
      <span className="text-[11.5px] font-bold text-gray-900 tracking-[0.06em]">
        {row.label}
      </span>
      <div className="pl-2 pr-2.5">
        <div
          className="h-1.5 bg-surface-sunken rounded-full relative overflow-hidden"
          aria-label={`${row.label} score: ${Math.round(score)}`}
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{ width: `${fill}%`, background: accent }}
            aria-hidden="true"
          />
        </div>
        {summary && (
          <div className="mt-0.5 text-[10.5px] text-gray-500 tabular-nums">
            {summary}
          </div>
        )}
      </div>
      <span
        className={`${mobile ? 'text-[14px]' : 'text-[15px]'} font-bold text-gray-900 tabular-nums text-right`}
      >
        {Math.round(score)}
      </span>
    </>
  );
}
